import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { Link } from "react-router-dom";

export default function BalanceByUser() {
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [summaryByDate, setSummaryByDate] = useState({});
  const [incomeBreakdownsByDate, setIncomeBreakdownsByDate] = useState({});
  const [openDates, setOpenDates] = useState({});

  const { data: salesData } = useGetSalesQuery();
  const { data: expensesData } = useGetExpensesQuery({ startDate, endDate });
  const { data: incomesData } = useGetIncomesQuery({ startDate, endDate });
  const { data: depositsData } = useGetDepositsQuery({ startDate, endDate });
  const { data: suppliesData } = useGetSuppliesQuery();

  const getDateRange = (start, end) => {
    const dates = [];
    let current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const filterByDate = (data, date) =>
    data?.filter((item) => item.date?.startsWith(date)) || [];

  const groupByUsername = (items, key) => {
    return items.reduce((acc, item) => {
      const username = item.username || "Unknown";
      acc[username] = (acc[username] || 0) + Number(item[key] || 0);
      return acc;
    }, {});
  };

  const groupIncomeByCategory = (items) => {
    const grouped = { Morning: 0, Afternoon: 0, Night: 0 };
    items.forEach((item) => {
      const category = item.category;
      const amount = Number(item.amount || 0);
      if (grouped.hasOwnProperty(category)) {
        grouped[category] += amount;
      }
    });
    return grouped;
  };

  const calculateSummary = () => {
    const dates = getDateRange(startDate, endDate);
    const summary = {};
    const breakdowns = {};

    dates.forEach((date) => {
      const sales = groupByUsername(filterByDate(salesData?.sales, date), "totalPrice");
      const expenses = groupByUsername(filterByDate(expensesData?.expenses, date), "amount");
      const incomes = filterByDate(incomesData?.incomes, date);
      const incomeTotals = groupByUsername(incomes, "amount");
      const deposits = groupByUsername(filterByDate(depositsData?.deposits, date), "amount");
      const supplies = groupByUsername(filterByDate(suppliesData?.supplies, date), "cardCost");

      breakdowns[date] = groupIncomeByCategory(incomes || []);

      const expensesForDate = filterByDate(expensesData?.expenses, date);

      const expenseCategoriesByUser = {};
      const expenseCategoryTotals = {};

      (expensesForDate || []).forEach((item) => {
        const cat = item.category || "Uncategorized";
        const user = item.username || "Unknown";
        const amt = Number(item.amount || 0);

        if (!expenseCategoriesByUser[cat]) expenseCategoriesByUser[cat] = {};
        if (!expenseCategoriesByUser[cat][user]) expenseCategoriesByUser[cat][user] = 0;

        expenseCategoriesByUser[cat][user] += amt;
        expenseCategoryTotals[cat] = (expenseCategoryTotals[cat] || 0) + amt;
      });

      const allUsernames = new Set([
        ...Object.keys(sales || {}),
        ...Object.keys(expenses || {}),
        ...Object.keys(incomeTotals || {}),
        ...Object.keys(deposits || {}),
        ...Object.keys(supplies || {}),
      ]);

      const rows = Array.from(allUsernames).map((username) => ({
        username,
        sales: sales?.[username] || 0,
        expenses: expenses?.[username] || 0,
        incomes: incomeTotals?.[username] || 0,
        deposits: deposits?.[username] || 0,
        supplies: supplies?.[username] || 0,
      }));

      const totals = rows.reduce(
        (acc, row) => {
          acc.sales += row.sales;
          acc.expenses += row.expenses;
          acc.incomes += row.incomes;
          acc.deposits += row.deposits;
          acc.supplies += row.supplies;
          acc.balance += row.incomes - (row.expenses + row.sales);
          return acc;
        },
        { sales: 0, expenses: 0, incomes: 0, deposits: 0, supplies: 0, balance: 0 }
      );

      summary[date] = {
        rows,
        totals,
        expenseCategoriesByUser,
        expenseCategoryTotals,
      };
    });

    setSummaryByDate(summary);
    setIncomeBreakdownsByDate(breakdowns);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    Object.entries(summaryByDate || {}).forEach(
      ([date, { rows, totals, expenseCategoriesByUser, expenseCategoryTotals }]) => {
        const sheetData = [
          ...(rows || []).map((row) => ({
            Date: date,
            User: row.username,
            Sales: row.sales.toFixed(2),
            Expenses: row.expenses.toFixed(2),
            Incomes: row.incomes.toFixed(2),
            Deposits: row.deposits.toFixed(2),
            Supplies: row.supplies.toFixed(2),
          })),
          {
            Date: date,
            User: "TOTAL",
            Sales: totals.sales.toFixed(2),
            Expenses: totals.expenses.toFixed(2),
            Incomes: totals.incomes.toFixed(2),
            Deposits: totals.deposits.toFixed(2),
            Supplies: totals.supplies.toFixed(2),
          },
        ];

        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, worksheet, date);

        const categorySheet = [];

        Object.entries(expenseCategoriesByUser || {}).forEach(([cat, users]) => {
          Object.entries(users || {}).forEach(([user, amt]) => {
            categorySheet.push({
              Category: cat,
              User: user,
              Amount: amt.toFixed(2),
            });
          });

          categorySheet.push({
            Category: `${cat} TOTAL`,
            User: "",
            Amount: (expenseCategoryTotals?.[cat] || 0).toFixed(2),
          });
        });

        categorySheet.push({
          Category: "GRAND TOTAL",
          User: "",
          Amount: Object.values(expenseCategoryTotals || {})
            .reduce((a, b) => a + b, 0)
            .toFixed(2),
        });

        const expenseWS = XLSX.utils.json_to_sheet(categorySheet);
        XLSX.utils.book_append_sheet(workbook, expenseWS, `${date}_ExpenseCategories`);
      }
    );

    XLSX.writeFile(workbook, `Summary_${startDate}_to_${endDate}.xlsx`);
  };

  const toggleDate = (date) => {
    setOpenDates((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  return (
    <div className="range-user-summary">
      <h2>Summary by User and Date</h2>

      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>

      <div className="actions">
        <button onClick={calculateSummary}>Generate Summary</button>
        <button onClick={exportToExcel} disabled={Object.keys(summaryByDate).length === 0}>
          Export to Excel
        </button>

        <Link to="/balancebycustomer" className="btn btn-light my-3">
          Sum By Customer
        </Link>
      </div>

      {Object.entries(summaryByDate || {}).map(
        ([date, { rows, totals, expenseCategoriesByUser, expenseCategoryTotals }]) => (
          <div key={date} className="daily-summary">
            <h3 onClick={() => toggleDate(date)} style={{ cursor: "pointer" }}>
              {date} {openDates[date] ? "▲" : "▼"}
            </h3>

            {openDates[date] && (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Sales</th>
                      <th>Ic/Exp</th>
                      <th>NetCash</th>
                      <th>Cashcolleted</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rows || []).map((row) => (
                      <tr key={row.username}>
                        <td>{row.username}</td>
                        <td>{row.sales.toFixed(2)}</td>
                        <td>{row.expenses.toFixed(2)}</td>
                        <td>{(row.sales - row.deposits + row.expenses).toFixed(2)}</td>
                        <td>{row.incomes.toFixed(2)}</td>
                        <td>{(row.sales - row.incomes + row.expenses).toFixed(2)}</td>
                      </tr>
                    ))}

                    <tr className="totals-row">
                      <td><strong>Total</strong></td>
                      <td>{totals.sales.toFixed(2)}</td>
                      <td>{totals.expenses.toFixed(2)}</td>
                      <td>{(totals.sales + totals.expenses).toFixed(2)}</td>
                      <td>{totals.incomes.toFixed(2)}</td>
                      <td>{totals.balance.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>

                <h4>Cash Breakdown by Shift</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(incomeBreakdownsByDate[date] || {}).map(
                      ([category, amount]) => (
                        <tr key={category}>
                          <td>{category}</td>
                          <td>{amount.toFixed(2)}</td>
                        </tr>
                      )
                    )}
                    <tr className="totals-row">
                      <td><strong>Total</strong></td>
                      <td>
                        {Object.values(incomeBreakdownsByDate[date] || {})
                          .reduce((sum, val) => sum + val, 0)
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h4>Source Breakdown by Category (User-Based)</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>User</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(expenseCategoriesByUser || {}).map(([cat, users]) => (
                      <React.Fragment key={cat}>
                        {Object.entries(users || {}).map(([user, amt]) => (
                          <tr key={`${cat}-${user}`}>
                            <td>{cat}</td>
                            <td>{user}</td>
                            <td>{amt.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="totals-row">
                          <td><strong>{cat} Total</strong></td>
                          <td></td>
                          <td>{(expenseCategoryTotals?.[cat] || 0).toFixed(2)}</td>
                        </tr>
                      </React.Fragment>
                    ))}

                    <tr className="totals-row">
                      <td><strong>Grand Total</strong></td>
                      <td></td>
                      <td>
                        {Object.values(expenseCategoryTotals || {})
                          .reduce((a, b) => a + b, 0)
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        )
      )}
    </div>
  );
}
